import React, { useEffect } from 'react'
import { Box, TextField, FormControlLabel, Checkbox, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import { object, string } from 'yup'

import { STEPS } from '../../../redux/modules/teamOnboarding'
import SteppedContent from '../../common/SteppedContent'
import { submitTeamDomainStart } from '../../../redux/modules/teamOnboarding'
import ButtonSubmit from '../../common/ButtonSubmit'
import {
  publishChangedDomain,
  publishChangedJoinabilitySetting,
  publishViewedDomainStep,
} from '../../../util/events/teamsOnboardingEventPublishers'

const schema = object().shape({
  teamDomain: string()
    .required('Company domain is required.')
    .matches(
      /\b((?=[a-z0-9-]{1,63}\.)[a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,63}\b/,
      'Please enter a valid domain.',
    ),
})

const useStyles = makeStyles(theme => ({
  atSign: {
    marginRight: theme.spacing(2),
    fontSize: '24px',
  },
  textField: {
    width: '320px',
  },
  formControlLabel: {
    marginRight: 0,
  },
}))

const StepTeamDomain = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSettingNextStep = useSelector(state => state.teamOnboarding.isSettingNextStep)
  const errorSettingNextStep = useSelector(state => state.teamOnboarding.errorSettingNextStep)

  const handleSubmit = ({ teamDomain, allowSelfJoin }) => {
    dispatch(submitTeamDomainStart(teamDomain, allowSelfJoin))
  }

  const handleBlurTeamDomain = (event, error, handleBlur) => {
    handleBlur(event)
    publishChangedDomain(event.target.value, error)
  }

  const handleChangeJoinability = (event, handleChange) => {
    handleChange(event)
    publishChangedJoinabilitySetting(event.target.checked)
  }

  useEffect(() => {
    publishViewedDomainStep()
  }, [])

  const showFeature = false // Flag to hide features that are not included in the MVP
  return (
    <SteppedContent
      width="720px"
      title="What's your company's email domain?"
      description="We use your company's email domain to help people on your team discover and join you on Foodsby."
      progressText={`Step ${STEPS.TEAM_DOMAIN} of 3`}
    >
      <Formik
        initialValues={{ teamDomain: '', allowSelfJoin: true }}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <Box display="flex" flexDirection="column" alignItems="center" width="100%">
              <Box display="flex" flexDirection="row" alignItems="center">
                <Typography className={classes.atSign}>@</Typography>
                <TextField
                  autoFocus
                  name="teamDomain"
                  className={classes.textField}
                  placeholder="company.com"
                  variant="outlined"
                  value={values.teamDomain}
                  onChange={handleChange}
                  onBlur={e => handleBlurTeamDomain(e, errors.teamDomain, handleBlur)}
                  error={touched.teamDomain && Boolean(errors.teamDomain)}
                  helperText={touched.teamDomain && errors.teamDomain}
                />
              </Box>
              {showFeature && (
                <Box width="320px" marginTop={4} textAlign="left">
                  <FormControlLabel
                    name="allowSelfJoin"
                    className={classes.formControlLabel}
                    control={<Checkbox color="primary" />}
                    label={`Let anyone with an ${values.teamDomain} email address join this team`}
                    value={values.allowSelfJoin}
                    onChange={e => handleChangeJoinability(e, handleChange)}
                    onBlur={handleBlur}
                  />
                </Box>
              )}
            </Box>
            <Box marginTop={4} marginBottom={2}>
              <ButtonSubmit variant="contained" color="primary" submitting={isSettingNextStep}>
                Next
              </ButtonSubmit>
            </Box>
            {errorSettingNextStep && (
              <Typography color="error">
                Sorry, there was a problem setting the team. Please try again or set it up later
                from your team management console.
              </Typography>
            )}
          </Form>
        )}
      </Formik>
    </SteppedContent>
  )
}

export default StepTeamDomain
